import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { AutoCompleteOptionItem } from '@autocomplete-with-controls';
import {
  AddressTypes,
  AvailableServices,
  ContactPerson,
  CounterpartyType,
  DoorAddress,
  WarehouseAddress,
} from '@models';
import { AuthService } from '@services';
import {
  CardTypes,
  DeliveryTypeExtended,
  GetCountriesData,
  GetIncotermsData,
  GetWarehousesData,
  WarehouseCategory,
} from '@shared';
import { UserTypeService } from 'app/services/user-type.service';
import { isPrivatePerson } from 'app/shared/utils/counterparty.utils';
import { BehaviorSubject, combineLatest, debounceTime, map, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  CargoType,
  DeliveryPayer,
  InvoicePlace,
  MeasurementsUnitExtended,
  PaymentType,
} from './components/international-invoice.interfaces';
import { CustomsFeePayer } from './components/invoice-information/invoice-information.constants';
import { CurrencyRates } from './components/invoice-information/invoice-information.models';
import { StoredTranslate, StoredTranslit } from './international-invoice.component';
import { IntPhoneMask } from './international-invoice.constants';

interface AdditionalExportInfo {
  additionalInfo: string;
  additionalInfoTranslit: string;
}

@Injectable()
export class InternationalInvoiceFormService {
  constructor(
    private authService: AuthService,
    private userTypeService: UserTypeService,
  ) {
  }

  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable();
  senderContactRef: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderContactRef$ = this.senderContactRef.asObservable();

  incotermSubstitutionDone: boolean = false;
  payerFeesCustomsSubstitutionDone: boolean = false;

  docNumber: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // By card type and getCounterpartyOptions
  counterpartyOptions: BehaviorSubject<AvailableServices> = new BehaviorSubject<AvailableServices>(null);
  recipientPayerAvailableByAccount: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  recipientPayerAvailableByCountryConfig: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  recipientPayerAvailableByRecipientType: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  deliveryTypes: Subject<DeliveryTypeExtended[]> = new Subject<DeliveryTypeExtended[]>();
  invoicePlaces: ReplaySubject<InvoicePlace[]> = new ReplaySubject<InvoicePlace[]>(1);
  allCurrencyRatesByAlfa3: BehaviorSubject<CurrencyRates> = new BehaviorSubject<CurrencyRates>({});
  deliveryType: Subject<{ type: DeliveryTypeExtended, total: any }> = new Subject<{
    type: DeliveryTypeExtended,
    total: any
  }>();
  exportTargetOptions: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  cargoTypes: ReplaySubject<CargoType[]> = new ReplaySubject<CargoType[]>(1);
  cargoType: BehaviorSubject<CargoType> = new BehaviorSubject<CargoType>(null);
  manufacturerCountries: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  parcelPlacesMeasurementUnits: ReplaySubject<MeasurementsUnitExtended[]> = new ReplaySubject<MeasurementsUnitExtended[]>(1);
  parcelDescription: BehaviorSubject<string> = new BehaviorSubject<string>('');
  paymentTypes: ReplaySubject<PaymentType[]> = new ReplaySubject<PaymentType[]>(1);
  paymentType: BehaviorSubject<PaymentType> = new BehaviorSubject<PaymentType>(null);
  deliveryPayerTypes: ReplaySubject<DeliveryPayer[]> = new ReplaySubject<DeliveryPayer[]>(1);
  deliveryPayer: BehaviorSubject<DeliveryPayer> = new BehaviorSubject<DeliveryPayer>(null);
  translits: Subject<StoredTranslit> = new Subject<StoredTranslit>();
  translates: BehaviorSubject<StoredTranslate> = new BehaviorSubject<StoredTranslate>({});
  createContactTriggered: Subject<void> = new Subject<void>();
  createContactCanceled: Subject<void> = new Subject<void>();
  disabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  senderContactsPending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  creationForbidden: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  incoterms: ReplaySubject<GetIncotermsData[]> = new ReplaySubject<GetIncotermsData[]>(1);
  incoterm: BehaviorSubject<GetIncotermsData> = new BehaviorSubject<GetIncotermsData>(null);
  payerFeesCustoms: BehaviorSubject<CustomsFeePayer> = new BehaviorSubject<CustomsFeePayer>(null);
  additionalExportInfo: BehaviorSubject<AdditionalExportInfo> = new BehaviorSubject<AdditionalExportInfo>({
    additionalInfo: '',
    additionalInfoTranslit: '',
  });

  senderOrganizations: Subject<ContactPerson[]> = new Subject<ContactPerson[]>();
  senderOrganizationExists: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  senderOrganizationLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  senderOrganizationChanged: BehaviorSubject<ContactPerson> = new BehaviorSubject<ContactPerson>(null);
  senderOrgRef: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderOrgSet: Subject<boolean> = new Subject<boolean>();

  stepSenderFormRawValue: BehaviorSubject<Record<string, any>> = new BehaviorSubject<Record<string, any>>(null);
  stepSenderFormRawValue$ = this.stepSenderFormRawValue.asObservable();

  senderOrganizations$ = this.senderOrganizations.asObservable();
  senderOrganizationExists$ = this.senderOrganizationExists.asObservable();
  senderOrganizationLoaded$ = this.senderOrganizationLoaded.asObservable();
  senderOrganizationChanged$ = this.senderOrganizationChanged.asObservable();
  senderOrgRef$ = this.senderOrgRef.asObservable();

  senderContacts: Subject<ContactPerson[]> = new Subject<ContactPerson[]>();
  currentSenderContactAddresses: BehaviorSubject<
    Array<AutoCompleteOptionItem<DoorAddress | WarehouseAddress>>
  > = new BehaviorSubject<
    Array<AutoCompleteOptionItem<DoorAddress | WarehouseAddress>>
  >(undefined);
  currentSenderContactDoorAddresses: BehaviorSubject<
    Array<AutoCompleteOptionItem<DoorAddress>>
  > = new BehaviorSubject<
    Array<AutoCompleteOptionItem<DoorAddress>>
  >(undefined);
  senderContacts$ = this.senderContacts.asObservable();
  currentSenderContactAddresses$ = this.currentSenderContactAddresses.asObservable();
  currentSenderContactDoorAddresses$ = this.currentSenderContactDoorAddresses.asObservable();

  senderAddressRef: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderRegistrationAddressRef: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderAddressRef$ = this.senderAddressRef.asObservable();
  senderRegistrationAddressRef$ = this.senderRegistrationAddressRef.asObservable();
  senderAddressSet: Subject<boolean> = new Subject<boolean>();
  senderRegistrationAddressSet: Subject<boolean> = new Subject<boolean>();
  senderIOSS: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderEORI: ReplaySubject<string> = new ReplaySubject<string>(1);
  orderNumber: ReplaySubject<string> = new ReplaySubject<string>(1);
  recipientEORI: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderIOSS$ = this.senderIOSS.asObservable();
  senderEORI$ = this.senderEORI.asObservable();
  orderNumber$ = this.orderNumber.asObservable();
  recipientEORI$ = this.recipientEORI.asObservable();

  editMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  duplicateMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  templateSubstitution: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  recipientCountry: ReplaySubject<GetCountriesData> = new ReplaySubject<GetCountriesData>(1);
  recipientCountryChanged: BehaviorSubject<GetCountriesData> = new BehaviorSubject<GetCountriesData>(null);
  recipientIndex: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPostalAddressIndex: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientState: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientCity: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPostalAddressCity: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientStreet: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPostalAddressStreet: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientBuilding: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPostalAddressBuilding: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientRoom: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPostalAddressRoom: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientNote: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientReceivingSpot: ReplaySubject<AddressTypes> = new ReplaySubject<AddressTypes>(1);
  recipientSelectedWarehouse: BehaviorSubject<GetWarehousesData> = new BehaviorSubject<GetWarehousesData>(null);
  recipientWarehouse: ReplaySubject<string> = new ReplaySubject<string>(1);
  recipientWarehouses: ReplaySubject<GetWarehousesData[] | null> = new ReplaySubject<GetWarehousesData[] | null>(1);
  recipientPostbox: ReplaySubject<any> = new ReplaySubject<any>(1);
  warehouseCategory: ReplaySubject<WarehouseCategory> = new ReplaySubject<WarehouseCategory>(1);
  recipientPhone: ReplaySubject<any> = new ReplaySubject<any>(1);
  recipientPhoneValidators: ReplaySubject<{
    validators: ValidatorFn[];
    errors: Record<string, string>;
  }> = new ReplaySubject<{ validators: ValidatorFn[]; errors: Record<string, string> }>(1);
  recipientPhonePrefix: BehaviorSubject<string> = new BehaviorSubject<string>('+');
  allowedWarehouseOwners: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(undefined);
  recipientPhoneMask: BehaviorSubject<Array<string | RegExp>> = new BehaviorSubject<Array<string | RegExp>>(
    IntPhoneMask,
  );
  recipientEmail: ReplaySubject<string> = new ReplaySubject<string>(1);
  senderEmail: ReplaySubject<string> = new ReplaySubject<string>(1);
  recipientNIP: ReplaySubject<string> = new ReplaySubject<string>(1);

  counterpartyOptions$ = this.counterpartyOptions.asObservable();
  recipientCountryChanged$ = this.recipientCountryChanged.asObservable();
  deliveryPayer$ = this.deliveryPayer.asObservable();

  recipientCounterpartyType: BehaviorSubject<CounterpartyType> = new BehaviorSubject(null);
  recipientCounterpartyType$ = this.recipientCounterpartyType.asObservable();
  recipientReceivingSpot$ = this.recipientReceivingSpot.asObservable();
  recipientSelectedWarehouse$ = this.recipientSelectedWarehouse.asObservable();

  // region Recipient Payer Availability

  recipientPayerAvailableByAccount$: Observable<boolean> = combineLatest([
    of(this.userTypeService.isLoyaltyRozniza()),
    this.counterpartyOptions$.pipe(filter(Boolean)),
  ]).pipe(
    map(([isLoyaltyRozniza, counterpartyOptions]) => {
      if (isLoyaltyRozniza) {
        return true;
      }
      return counterpartyOptions.PayerRecipientInternational;
    }),
  );

  recipientPayerAvailableByCountryConfig$ = this.recipientCountryChanged$
    .pipe(
      map(country => !!(country
        && country.RecipientPayerAllowed
        && country.RecipientSpotSelectionAvailable),
      ),
    );

  recipientPayerAvailableByCountryConfigOverride$ = this.recipientCountryChanged$
    .pipe(
      map(country => !!(country && country.RecipientPayerAllowedOverride)),
    );

  showDeliveryPrice$ = combineLatest([
    this.recipientPayerAvailableByCountryConfigOverride$,
    this.deliveryPayer$,
  ])
    .pipe(
      map(([override, payer]) => override || payer?.DescriptionEng === 'Shipper'),
    );

  recipientPayerAvailableByRecipientType$ = this.recipientCounterpartyType$
    .pipe(
      map(counterpartyType => isPrivatePerson(counterpartyType)),
    );

  recipientPayerAvailableByReceivingSpot$ = this.recipientReceivingSpot$
    .pipe(
      map(receivingSpot => receivingSpot === AddressTypes.WAREHOUSE),
    );

  recipientPayerAvailableByWarehouseOwner$ = this.recipientSelectedWarehouse$
    .pipe(
      map(warehouse => warehouse?.WarehouseOwner === 'NPAX'),
    );

  recipientPayerAvailable$: Observable<boolean> = combineLatest([
    this.recipientPayerAvailableByAccount$,
    this.recipientPayerAvailableByCountryConfig$,
    this.recipientPayerAvailableByReceivingSpot$,
    this.recipientPayerAvailableByWarehouseOwner$,
    this.recipientPayerAvailableByRecipientType$,
    this.recipientPayerAvailableByCountryConfigOverride$,
  ]).pipe(
    map(([
      byAccount,
      byCountryConfig,
      byReceivingSpot,
      byWarehouseOwner,
      byRecipientType,
      byCountryConfigOverride,
    ]) => (
        byAccount && byCountryConfig && byReceivingSpot && byWarehouseOwner && byRecipientType
      )
      // Override by country config skips other checks (like for MD)
      || byCountryConfigOverride),
  );

  // endregion Recipient Payer Availability

  deliveryTypes$ = this.deliveryTypes.asObservable();
  invoicePlaces$ = this.invoicePlaces.asObservable();
  deliveryType$ = this.deliveryType.asObservable();
  exportTargetOptions$ = this.exportTargetOptions.asObservable();
  cargoTypes$ = this.cargoTypes.asObservable();
  manufacturerCountries$ = this.manufacturerCountries.asObservable();
  parcelPlacesMeasurementUnits$ = this.parcelPlacesMeasurementUnits.asObservable();
  paymentTypes$ = this.paymentTypes.asObservable();
  paymentType$ = this.paymentType.asObservable();
  deliveryPayerTypes$ = this.deliveryPayerTypes.asObservable();
  translits$ = this.translits.asObservable();
  translates$ = this.translates.asObservable();
  createContactTriggered$ = this.createContactTriggered.asObservable();
  createContactCanceled$ = this.createContactCanceled.asObservable();
  senderContactsPending$ = this.senderContactsPending.asObservable();
  creationForbidden$ = this.creationForbidden.asObservable();
  incoterm$ = this.incoterm.asObservable();
  payerFeesCustoms$ = this.payerFeesCustoms.asObservable();
  incoterms$ = this.incoterms.asObservable();
  disabled$ = combineLatest([
    this.creationForbidden$,
    this.senderContactsPending$,
  ]).pipe(
    // To avoid button flicker
    debounceTime(200),
    map(([a, b]) => a || b),
  );

  recipientCountry$ = this.recipientCountry.asObservable();
  recipientIndex$ = this.recipientIndex.asObservable();
  recipientPostalAddressIndex$ = this.recipientPostalAddressIndex.asObservable();
  recipientState$ = this.recipientState.asObservable();
  recipientCity$ = this.recipientCity.asObservable();
  recipientPostalAddressCity$ = this.recipientPostalAddressCity.asObservable();
  recipientStreet$ = this.recipientStreet.asObservable();
  recipientPostalAddressStreet$ = this.recipientPostalAddressStreet.asObservable();
  recipientBuilding$ = this.recipientBuilding.asObservable();
  recipientPostalAddressBuilding$ = this.recipientPostalAddressBuilding.asObservable();
  recipientRoom$ = this.recipientRoom.asObservable();
  recipientPostalAddressRoom$ = this.recipientPostalAddressRoom.asObservable();
  recipientNote$ = this.recipientNote.asObservable();
  recipientWarehouse$ = this.recipientWarehouse.asObservable();
  recipientWarehouses$ = this.recipientWarehouses.asObservable();
  recipientPostbox$ = this.recipientPostbox.asObservable();
  warehouseCategory$ = this.warehouseCategory.asObservable();
  recipientPhone$ = this.recipientPhone.asObservable();
  recipientPhoneValidators$ = this.recipientPhoneValidators.asObservable();
  recipientEmail$ = this.recipientEmail.asObservable();
  senderEmail$ = this.senderEmail.asObservable();
  recipientNIP$ = this.recipientNIP.asObservable();

  allowSenderTypeAddress = this.authService.user.loyaltyCardType !== CardTypes.ROZNIZA;

  setLoading(value: boolean): void {
    this.loading.next(value);
  }
}
